import { Issue, IssuePriority, SlaTimers } from "../types";

export interface ResolutionMetrics {
  durationMs: number;
  totalMinutes: number;
  formatted: string;
  resolvedAt: string;
}

/**
 * Calculates Time to Resolution (TTR) based on createdAt and resolvedAt (or closedAt fallback).
 */
export function calculateTimeToResolution(
  issue: Pick<Issue, "createdAt" | "status"> & {
    resolvedAt?: string | null;
    closedAt?: string | null;
  },
): ResolutionMetrics | null {
  const effectiveResolvedAt =
    issue.resolvedAt ||
    (issue.status === "resolved" || issue.status === "closed"
      ? issue.closedAt
      : null);

  if (!effectiveResolvedAt || !issue.createdAt) return null;

  const start = new Date(issue.createdAt).getTime();
  const end = new Date(effectiveResolvedAt).getTime();
  if (isNaN(start) || isNaN(end)) return null;

  const diffMs = Math.max(0, end - start);
  const totalMinutes = Math.floor(diffMs / 60000);

  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const mins = totalMinutes % 60;

  let formatted = "";
  if (days > 0) {
    formatted = `${days}d ${hours}h`;
  } else if (hours > 0) {
    formatted = `${hours}h ${mins}m`;
  } else {
    formatted = `${mins}m`;
  }

  return {
    durationMs: diffMs,
    totalMinutes,
    formatted,
    resolvedAt: effectiveResolvedAt,
  };
}

export interface SlaCalculationResult {
  targetAssignTime: string;
  targetWarnTime: string;
  totalBusinessMinutes: number;
}

const SLA_HOURS_MAP: Record<IssuePriority, number> = {
  critical: 1,
  high: 4,
  medium: 24, // 3 business days (8h/day)
  low: 48, // 6 business days (8h/day)
};

/**
 * Calculates Chicago business hours SLA deadlines (Monday - Friday, 09:00 - 17:00 America/Chicago).
 */
export function calculateSlaDeadlines(
  startDate: Date = new Date(),
  priority: IssuePriority = "medium",
): SlaTimers {
  const targetHours = SLA_HOURS_MAP[priority] ?? 24;
  const targetMinutes = targetHours * 60;
  const warnMinutes = Math.floor(targetMinutes * 0.75);

  const assignDate = addBusinessMinutes(startDate, targetMinutes);
  const warnDate = addBusinessMinutes(startDate, warnMinutes);

  return {
    targetAssignTime: assignDate.toISOString(),
    targetWarnTime: warnDate.toISOString(),
    status: "on_track",
    assignedAt: null,
    slaWarnedAt: null,
    slaBreachedAt: null,
  };
}

/**
 * Adds business minutes step-by-step skipping non-business hours (before 9am, after 5pm, weekends).
 */
export function addBusinessMinutes(
  startDate: Date,
  minutesToAdd: number,
): Date {
  const current = new Date(startDate.getTime());
  let remainingMinutes = minutesToAdd;

  while (remainingMinutes > 0) {
    const dayOfWeek = current.getDay(); // 0 is Sunday, 6 is Saturday
    const hours = current.getHours();
    const minutes = current.getMinutes();

    // If Weekend (Saturday or Sunday), advance to Monday 09:00
    if (dayOfWeek === 0) {
      current.setDate(current.getDate() + 1);
      current.setHours(9, 0, 0, 0);
      continue;
    }
    if (dayOfWeek === 6) {
      current.setDate(current.getDate() + 2);
      current.setHours(9, 0, 0, 0);
      continue;
    }

    // If before 09:00 on a weekday, jump to 09:00
    if (hours < 9) {
      current.setHours(9, 0, 0, 0);
      continue;
    }

    // If at or after 17:00 on a weekday, jump to next day 09:00
    if (hours >= 17) {
      current.setDate(current.getDate() + 1);
      current.setHours(9, 0, 0, 0);
      continue;
    }

    // Calculate how many business minutes remain in current day before 17:00
    const minutesUntilEndOfDay = (17 - hours) * 60 - minutes;

    if (remainingMinutes <= minutesUntilEndOfDay) {
      current.setMinutes(current.getMinutes() + remainingMinutes);
      remainingMinutes = 0;
    } else {
      remainingMinutes -= minutesUntilEndOfDay;
      current.setDate(current.getDate() + 1);
      current.setHours(9, 0, 0, 0);
    }
  }

  return current;
}

/**
 * Formats time remaining until SLA target.
 */
export function formatSlaCountdown(
  targetTimeStr: string,
  now: Date = new Date(),
): {
  text: string;
  isBreached: boolean;
  isWarning: boolean;
} {
  const target = new Date(targetTimeStr);
  const diffMs = target.getTime() - now.getTime();

  if (diffMs <= 0) {
    const elapsedMinutes = Math.abs(Math.floor(diffMs / 60000));
    const hours = Math.floor(elapsedMinutes / 60);
    const mins = elapsedMinutes % 60;
    return {
      text:
        hours > 0 ? `Breached by ${hours}h ${mins}m` : `Breached by ${mins}m`,
      isBreached: true,
      isWarning: false,
    };
  }

  const remainingMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(remainingMinutes / 60);
  const mins = remainingMinutes % 60;

  return {
    text: hours > 0 ? `${hours}h ${mins}m left` : `${mins}m left`,
    isBreached: false,
    isWarning: remainingMinutes < 60, // less than 1 hour remaining
  };
}
